import { useState, useEffect, useRef } from 'react';
import { Check, X, Loader2, MessageSquare, CreditCard } from 'lucide-react';
import { api } from '@/App';

/* ===== ANIMATED CLOCK ICON ===== */
function ClockIcon() {
  return (
    <div className="relative w-20 h-20 mx-auto mb-5">
      <div className="w-20 h-20 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-white">
          <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5"
            strokeDasharray="60" strokeDashoffset="15"
            className="animate-[spin_3s_linear_infinite]" style={{ transformOrigin: '12px 12px' }} />
          <line x1="12" y1="12" x2="12" y2="7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            className="animate-[spin_4s_linear_infinite]" style={{ transformOrigin: '12px 12px' }} />
          <line x1="12" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
            className="animate-[spin_12s_linear_infinite]" style={{ transformOrigin: '12px 12px' }} />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}

/* ===== REJECTED X ICON ===== */
function RejectedIcon() {
  return (
    <div className="relative w-20 h-20 mx-auto mb-5">
      <div className="w-20 h-20 rounded-full flex items-center justify-center border-[3px] border-red-500 bg-red-500/10">
        <X className="w-10 h-10 text-red-500" strokeWidth={2.5} />
      </div>
    </div>
  );
}

/* ===== APPROVED CHECK ICON WITH GLOW ===== */
function ApprovedIcon({ animate }) {
  return (
    <div className="relative w-20 h-20 mx-auto mb-5">
      {/* Glow rings */}
      <div className={`absolute inset-0 rounded-full bg-green-500/20 ${animate ? 'animate-ping' : ''}`}
        style={{ animationDuration: '2s', animationIterationCount: '2' }} />
      <div className="absolute inset-2 rounded-full bg-green-500/10" />
      <div className="relative w-20 h-20 rounded-full flex items-center justify-center bg-green-500">
        <Check className={`w-10 h-10 text-white ${animate ? 'animate-bounce' : ''}`}
          strokeWidth={3} style={{ animationDuration: '0.6s', animationIterationCount: '3' }} />
      </div>
    </div>
  );
}

/* ===== PROPERTY INFO CARD ===== */
function PropertyInfoCard({ request }) {
  const adults = request.adults || request.guests?.adults || 1;
  const children = request.children || request.guests?.children || 0;
  return (
    <div className="bg-[#F5F4F0] dark:bg-[#1a1a1a] rounded-xl p-4 mb-5 border border-[#E7E5E4] dark:border-[#333]">
      <p className="font-bold text-sm text-[#1C1917] dark:text-white">{request.property_title}</p>
      <p className="text-xs text-[#57534E] dark:text-[#A8A29E] mt-1">
        Fechas: {request.check_in} — {request.check_out}
      </p>
      <p className="text-xs text-[#57534E] dark:text-[#A8A29E]">
        Huéspedes: {adults} adulto{adults > 1 ? 's' : ''}{children > 0 ? `, ${children} niño${children > 1 ? 's' : ''}` : ''}
      </p>
    </div>
  );
}

/* ===== MAIN COMPONENT ===== */
export function BookingStatusBanner({ requestData, propertyId, onDismiss }) {
  const [currentRequest, setCurrentRequest] = useState(requestData);
  const [paying, setPaying] = useState(false);
  const [justApproved, setJustApproved] = useState(false);
  const pollRef = useRef(null);
  const prevStatusRef = useRef(requestData?.status);

  // Use the currency stored in the booking request itself
  const requestCurrency = (currentRequest?.currency || 'PYG').toUpperCase();

  // Poll for status changes every 5 seconds
  useEffect(() => {
    if (!currentRequest?.request_id) return;
    const status = currentRequest.status;
    if (!['pending_verification', 'approved'].includes(status)) return;

    const poll = async () => {
      try {
        const res = await api.get(`/booking-requests/by-property/${propertyId}`);
        const found = (res.data.requests || []).find(r => r.request_id === currentRequest.request_id);
        if (found && found.status !== prevStatusRef.current) {
          if (found.status === 'approved' && prevStatusRef.current === 'pending_verification') {
            setJustApproved(true);
            setTimeout(() => setJustApproved(false), 5000);
          }
          prevStatusRef.current = found.status;
          setCurrentRequest(found);
        }
      } catch (_) { /* silent */ }
    };

    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [currentRequest?.request_id, currentRequest?.status, propertyId]);

  const handlePay = async () => {
    if (!currentRequest) return;
    setPaying(true);
    try {
      if (requestCurrency === 'ARS') {
        // ARS → MercadoPago
        const res = await api.post(`/booking-requests/${currentRequest.request_id}/checkout-mp`);
        const url = res.data.checkout_url;
        if (url) window.location.href = url;
        else setPaying(false);
      } else {
        // PYG / USD → Bancard vPOS
        const res = await api.post(`/booking-requests/${currentRequest.request_id}/checkout-bancard`);
        const { process_id, bancard_env, amount, request_id } = res.data;
        if (process_id) {
          window.location.href = `/pago-bancard?process_id=${process_id}&env=${bancard_env}&amount=${amount}&request_id=${request_id}`;
        } else {
          setPaying(false);
        }
      }
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || 'Error al procesar el pago');
      setPaying(false);
    }
  };

  if (!currentRequest) return null;
  const { status } = currentRequest;

  // ==================== PENDING VERIFICATION ====================
  if (status === 'pending_verification') {
    return (
      <div className="bg-white dark:bg-[#1C1C1E] border border-[#E7E5E4] dark:border-[#333] rounded-2xl p-6 shadow-xl text-center relative" data-testid="booking-status-pending">
        <ClockIcon />

        <h3 className="text-xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          Verificando Disponibilidad
        </h3>
        <p className="text-sm text-[#57534E] dark:text-[#A8A29E] mb-5">
          Estamos verificando disponibilidad para tu solicitud. Te confirmaremos a la brevedad.
        </p>

        <PropertyInfoCard request={currentRequest} />

        <p className="text-xs text-[#78716C] dark:text-[#888] mb-5">
          No refresques la web — aguardanos.
        </p>

        <div className="flex gap-3">
          <a href={`https://wa.me/${currentRequest.whatsapp_contact?.replace('+', '') || '595982188147'}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#1DA851] transition-colors text-sm font-semibold"
            data-testid="status-whatsapp-btn">
            <MessageSquare className="w-5 h-5" /> Consultar por WhatsApp
          </a>
          {onDismiss && (
            <button onClick={onDismiss}
              className="px-5 py-3 border border-[#D6D3D1] dark:border-[#444] text-[#57534E] dark:text-[#A8A29E] rounded-xl hover:bg-[#F5F4F0] dark:hover:bg-[#292524] transition-colors text-sm font-semibold"
              data-testid="status-close-btn">
              Cerrar
            </button>
          )}
        </div>
      </div>
    );
  }

  // ==================== REJECTED ====================
  if (status === 'rejected') {
    return (
      <div className="bg-white dark:bg-[#1C1C1E] border border-[#E7E5E4] dark:border-[#333] rounded-2xl p-6 shadow-xl text-center relative" data-testid="booking-status-rejected">
        <RejectedIcon />

        <h3 className="text-xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          Disponibilidad no aprobada
        </h3>
        <p className="text-sm text-[#57534E] dark:text-[#A8A29E] mb-5 max-w-xs mx-auto">
          En este momento no pudimos confirmar la disponibilidad para tu solicitud. Comunicate con nosotros y te ayudamos con una alternativa.
        </p>

        <PropertyInfoCard request={currentRequest} />

        <p className="text-xs text-[#78716C] dark:text-[#888] mb-5 max-w-xs mx-auto">
          Recibimos tu solicitud y te notificaremos por email y en la plataforma si se libera disponibilidad o si elegís otra opción.
        </p>

        <div className="flex gap-3">
          <a href={`https://wa.me/${currentRequest.whatsapp_contact?.replace('+', '') || '595982188147'}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#1DA851] transition-colors text-sm font-semibold"
            data-testid="status-whatsapp-btn">
            <MessageSquare className="w-5 h-5" /> Contactar por WhatsApp
          </a>
          {onDismiss && (
            <button onClick={onDismiss}
              className="px-5 py-3 border border-[#D6D3D1] dark:border-[#444] text-[#57534E] dark:text-[#A8A29E] rounded-xl hover:bg-[#F5F4F0] dark:hover:bg-[#292524] transition-colors text-sm font-semibold"
              data-testid="status-close-btn">
              Cerrar
            </button>
          )}
        </div>
      </div>
    );
  }

  // ==================== APPROVED ====================
  if (status === 'approved') {
    return (
      <div className="bg-white dark:bg-[#1C1C1E] border border-[#E7E5E4] dark:border-[#333] rounded-2xl p-6 shadow-xl text-center relative" data-testid="booking-status-approved">
        <ApprovedIcon animate={justApproved} />

        <h3 className="text-xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          Reserva aprobada
        </h3>
        <p className="text-sm text-[#57534E] dark:text-[#A8A29E] mb-5 max-w-xs mx-auto">
          La disponibilidad de tu solicitud fue confirmada. Ya podés completar el pago para asegurar tu reserva.
        </p>

        <PropertyInfoCard request={currentRequest} />

        <p className="text-xs text-[#78716C] dark:text-[#888] mb-5 max-w-xs mx-auto">
          Recibimos tu solicitud y te notificaremos por email y en la plataforma una vez realizado el pago.
        </p>

        <div className="flex gap-3">
          <button onClick={handlePay} disabled={paying}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl transition-colors text-sm font-semibold disabled:opacity-60"
            data-testid="pay-now-btn">
            {paying
              ? <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</>
              : <><CreditCard className="w-5 h-5" /> Pagar ahora</>}
          </button>
          {onDismiss && (
            <button onClick={onDismiss}
              className="px-5 py-3 border border-[#D6D3D1] dark:border-[#444] text-[#57534E] dark:text-[#A8A29E] rounded-xl hover:bg-[#F5F4F0] dark:hover:bg-[#292524] transition-colors text-sm font-semibold"
              data-testid="status-close-btn">
              Cerrar
            </button>
          )}
        </div>
      </div>
    );
  }

  // ==================== PAYING ====================
  if (status === 'paying') {
    return (
      <div className="bg-white dark:bg-[#1C1C1E] border border-[#E7E5E4] dark:border-[#333] rounded-2xl p-6 shadow-xl text-center" data-testid="booking-status-paying">
        <div className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center bg-blue-500/10 border-2 border-blue-400">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        </div>
        <h3 className="text-xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          Procesando Pago
        </h3>
        <p className="text-sm text-[#57534E] dark:text-[#A8A29E]">
          Estamos procesando tu pago. No cierres esta página.
        </p>
      </div>
    );
  }

  // ==================== PAID ====================
  if (status === 'paid') {
    return (
      <div className="bg-white dark:bg-[#1C1C1E] border border-[#E7E5E4] dark:border-[#333] rounded-2xl p-6 shadow-xl text-center" data-testid="booking-status-paid">
        <ApprovedIcon animate={false} />
        <h3 className="text-xl font-bold text-green-700 dark:text-green-400 mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          ¡Reserva Confirmada!
        </h3>
        <p className="text-sm text-[#57534E] dark:text-[#A8A29E]">
          Tu pago fue procesado exitosamente. ¡Preparate para tu estadía!
        </p>
      </div>
    );
  }

  return null;
}
